import { useEffect, useMemo, useState } from "react";
import { useQueryWithState, type QueryState } from "./hooks/useQueryWithState";
import { api } from "./convex-api/api";
import type { IntelData } from "./lib/translate";
import { resolveIntelData } from "./lib/dataResolution";
import { mergeRealtimeMarketData, realtimeSymbolsForMerge } from "./lib/realtimeMarketData";
import {
  buildCommandCenterDataQualityRows,
  buildCommandCenterDataSourceRows,
  type CommandCenterDataQualityRow,
  type CommandCenterDataSourceRow,
} from "./lib/commandCenterViewModel";
import { CommandCenterV2 } from "./components/commandCenterV2/CommandCenterV2";
import { DASHBOARD_SYMBOLS, LIVE_INTEL_STALE_AFTER_MS, dashboardDataStatusFromFreshness, freshnessFromTimestamp, isLiveSnapshotAgent } from "./lib/dashboardDataContract";
import type { LevelBounceForecastSnapshot } from "./components/LevelBounceForecastPanel";
import { ProfitabilityInsightsPanel } from "./components/ProfitabilityInsightsPanel";
import { CommandCenterDataWiringPanel } from "./components/CommandCenterDataWiringPanel";
import { CommandCenterDataQualityPanel } from "./components/CommandCenterDataQualityPanel";
import { CommandCenterDataReadinessPanel } from "./components/CommandCenterDataReadinessPanel";
import { CommandCenterActionMatrix } from "./components/CommandCenterActionMatrix";
import { FbeAccountabilityPanelContainer } from "./components/FbeAccountabilityPanel";
import { ExecutionAccountabilityPanel } from "./components/ExecutionAccountabilityPanel";
import { AgentAccountabilityPanel, type AgentAccountabilityConfig } from "./components/AgentAccountabilityPanel";
import { AccountabilityGovernancePanel } from "./components/AccountabilityGovernancePanel";
import { CtoReviewWorkspace } from "./components/CtoReviewWorkspace";
import { RegimeValidationPanel } from "./components/RegimeValidationPanel";
import { MarkovWorkspace } from "./components/markov/MarkovWorkspace";
import { FbeBiasWorkspace } from "./components/fbeBias/FbeBiasWorkspace";
import { LoginPage } from "./components/LoginPage";
import { useRealtimeMarketFeatures } from "./hooks/useRealtimeMarketFeatures";
import { activeSessionKey } from "./lib/sessionClock";
import "./App.css";

/* ── Types ── */

interface Signal {
  _id: string;
  agent: string;
  session: string;
  date: string;
  symbol: string;
  direction: string;
  entry: number;
  stop_loss: number;
  tp1: number;
  tp2?: number;
  score?: number;
  outcome?: string;
  pnl_dollars?: number;
  hmm_regime?: string;
  hmm_probability?: number;
  kronos_direction?: string;
  kronos_confidence?: number;
  kronos_daily_method?: string;
  kronos_daily_input_anchor_source?: string;
  kronos_daily_change_pct?: number;
  kronos_daily_predicted_close?: number;
  kronos_daily_predicted_high?: number;
  kronos_daily_predicted_low?: number;
  kronos_4h_direction?: string;
  kronos_4h_confidence?: number;
  kronos_4h_method?: string;
  kronos_4h_source?: string;
  kronos_4h_predicted_open?: number;
  kronos_4h_predicted_close?: number;
  kronos_4h_predicted_high?: number;
  kronos_4h_predicted_low?: number;
  kronos_4h_range?: number;
  kronos_4h_input_anchor_as_of?: number;
  kronos_4h_input_anchor_price?: number;
  kronos_4h_input_anchor_source?: string;
  kronos_4h_input_anchor_age_sec?: number;
  kronos_4h_anchor_status?: string;
  kronos_4h_anchor_rebased?: boolean;
  kronos_4h_pre_rebase_predicted_open?: number;
  kronos_4h_open_anchor_gap_pct?: number;
  kronos_4h_anchor_rebase_delta?: number;
  kronos_hourly_direction?: string;
  kronos_hourly_confidence?: number;
  kronos_hourly_method?: string;
  kronos_hourly_predicted_open?: number;
  kronos_hourly_predicted_close?: number;
  kronos_hourly_predicted_high?: number;
  kronos_hourly_predicted_low?: number;
  kronos_hourly_range?: number;
  kronos_hourly_input_anchor_as_of?: number;
  kronos_hourly_input_anchor_price?: number;
  kronos_hourly_input_anchor_source?: string;
  kronos_hourly_input_anchor_age_sec?: number;
  kronos_hourly_anchor_status?: string;
  kronos_hourly_anchor_rebased?: boolean;
  kronos_hourly_pre_rebase_predicted_open?: number;
  kronos_hourly_open_anchor_gap_pct?: number;
  kronos_hourly_anchor_rebase_delta?: number;
  hmm_4h_regime?: string;
  hmm_4h_probability?: number;
  hmm_hourly_regime?: string;
  hmm_hourly_probability?: number;
  hmm_composite_regime?: string;
  hmm_composite_confidence?: number;
  squeeze_on?: boolean;
  squeeze_fired?: boolean;
  squeeze_momentum?: number;
  squeeze_stage?: string;
  squeeze_momentum_direction?: string;
  // Multi-timeframe squeeze
  squeeze_1h_on?: boolean;
  squeeze_1h_fired?: boolean;
  squeeze_1h_direction?: string;
  squeeze_1h_stage?: string;
  squeeze_1h_pct?: number;
  squeeze_1h_pro_level?: string;
  squeeze_1h_status?: string;
  squeeze_1h_status_reason?: string;
  squeeze_4h_on?: boolean;
  squeeze_4h_fired?: boolean;
  squeeze_4h_direction?: string;
  squeeze_4h_stage?: string;
  squeeze_4h_pct?: number;
  squeeze_4h_pro_level?: string;
  squeeze_4h_status?: string;
  squeeze_4h_status_reason?: string;
  squeeze_daily_on?: boolean;
  squeeze_daily_fired?: boolean;
  squeeze_daily_direction?: string;
  squeeze_daily_stage?: string;
  squeeze_daily_pct?: number;
  squeeze_daily_pro_level?: string;
  squeeze_daily_status?: string;
  squeeze_daily_status_reason?: string;
  tv_rating?: string;
  adx?: number;
  adx_label?: string;
  adx_plus_di?: number;
  adx_minus_di?: number;
  vix?: number;
  vix_regime?: string;
  vix_ma10?: number;
  vix_ma20?: number;
  vix_slope?: string;
  intel_stance?: string;
  intel_alignment_score?: number;
  intel_layers_for?: string;
  intel_layers_against?: string;
  vol_ratio?: number;
  sma50_value?: number;
  sma50_aligned?: boolean | null;
  ema50_value?: number;
  ema50_aligned?: boolean | null;
  // QST-334: Volume flow indicators (LOG ONLY)
  obv_divergence_10?: string;
  obv_divergence_20?: string;
  cmf_20?: number;
  mfi_14?: number;
  fbd_flow_score?: number;
  mr_flow_score?: number;
  // QST-320: Realtime order flow indicators
  iceberg_bid?: boolean;
  iceberg_ask?: boolean;
  absorption_bid?: boolean;
  absorption_ask?: boolean;
  institutional_direction?: string;
  institutional_score?: number;
  spoof_bid_risk?: number;
  spoof_ask_risk?: number;
  order_flow_status?: string;
  order_flow_age_sec?: number;
  // QST-343 / FBE-3: Forecast Belief Engine fields persisted in
  // liveMarketSnapshots.payload by QST-342. Display-only on the dashboard.
  forecast_raw_state?: string;
  forecast_raw_confidence?: number;
  forecast_raw_probs?: Record<string, number>;
  forecast_stable_state?: string;
  forecast_stable_confidence?: number;
  forecast_transition_probability?: number;
  forecast_state_age_min?: number;
  forecast_flip_suppressed?: boolean;
  forecast_stability_score?: number;
  forecast_reason?: string;
  forecast_engine_version?: string;
  forecast_generated_at?: number;
  forecast_generated_at_iso?: string;
  forecast_contract?: string;
  forecast_session_id?: string;
  forecast_anchor_type?: string;
  forecast_anchor_price?: number;
  forecast_anchor_preserved?: boolean;
  forecast_authoritative_channel?: string;
  forecast_candidate_channel?: string;
  forecast_release_policy?: string;
  forecast_accuracy_status?: string;
  forecast_data_integrity_status?: string;
  forecast_data_integrity_issues?: string;
  forecast_trust_status?: string;
  forecast_trading_approved?: boolean;
  forecast_calibration_sample_size?: number;
  forecast_calibration_wins?: number;
  forecast_calibration_losses?: number;
  forecast_calibration_flats?: number;
  forecast_calibration_win_rate?: number;
  forecast_calibration_avg_brier?: number;
  forecast_calibration_horizon_results?: Record<string, Record<string, number>>;
  candidate_forecast_raw_state?: string;
  candidate_forecast_raw_confidence?: number;
  candidate_forecast_raw_probs?: Record<string, number>;
  candidate_forecast_stable_state?: string;
  candidate_forecast_stable_confidence?: number;
  candidate_forecast_transition_probability?: number;
  candidate_forecast_state_age_min?: number;
  candidate_forecast_flip_suppressed?: boolean;
  candidate_forecast_stability_score?: number;
  candidate_forecast_reason?: string;
  candidate_forecast_engine_version?: string;
  candidate_forecast_contract?: string;
  candidate_forecast_session_id?: string;
  candidate_forecast_anchor_type?: string;
  candidate_forecast_anchor_price?: number;
  candidate_forecast_anchor_preserved?: boolean;
  forecast_session_start_ms?: number;
  forecast_session_end_ms?: number;
  candidate_forecast_session_start_ms?: number;
  candidate_forecast_session_end_ms?: number;
  candidate_forecast_calibration_sample_size?: number;
  candidate_forecast_calibration_win_rate?: number;
  candidate_forecast_calibration_avg_brier?: number;
  created_at: number;
}

/** Stats from Convex — mirrors convex/queries.ts::getStats return type.
 *  IMPORTANT: win_rate = wins / (wins + losses), EXCLUDING breakevens (QST-228). */
interface Stats {
  total_signals: number;
  closed_trades: number;
  wins: number;
  losses: number;
  breakevens: number;
  win_rate: number;
  total_pnl: number;
  open_trades: number;
  by_agent: Record<string, number>;
}

interface OpenTrade {
  _id: string;
  agent: string;
  session: string;
  symbol: string;
  direction: string;
  entry_price: number;
  stop_loss: number;
  tp1: number;
  tp2?: number;
  status: string;
  opened_at?: number;
  created_at: number;
}

/* ── Session Timer ── */

function getSessionTimes() {
  const now = new Date();
  const utcH = now.getUTCHours();
  const utcM = now.getUTCMinutes();

  // Asia OR: 00:00–00:30 UTC
  if (utcH === 0 && utcM < 30) return { current: "Asia", next: null };
  // US OR: 13:30–14:00 UTC
  if (utcH === 13 && utcM >= 30) return { current: "US", next: null };
  if (utcH === 14 && utcM < 1) return { current: "US", next: null };

  // Calculate next session
  const asiaStart = new Date(now);
  asiaStart.setUTCHours(0, 0, 0, 0);
  if (asiaStart <= now) asiaStart.setUTCDate(asiaStart.getUTCDate() + 1);

  const usStart = new Date(now);
  usStart.setUTCHours(13, 30, 0, 0);
  if (usStart <= now) usStart.setUTCDate(usStart.getUTCDate() + 1);

  const next = asiaStart <= usStart
    ? { name: "Asia", time: asiaStart }
    : { name: "US", time: usStart };

  // Check if currently in active session window
  const currentKey = activeSessionKey(now);
  const current = currentKey === "asia" ? "Asia" as const
    : currentKey === "us" ? "US" as const
    : null;

  return { current, next };
}

function CountdownTimer() {
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(id); }, []);

  const { current, next } = getSessionTimes();
  if (current) {
    const label = current === "Asia" ? "🌏 Asia" : "🇺🇸 New York";
    return <span className="session-active">{label} session active</span>;
  }
  if (!next) return null;

  const diff = next.time.getTime() - Date.now();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);

  return (
    <span className="session-next">
      Next: {next.name} in {h}h {m}m {s}s
    </span>
  );
}


function rootSymbol(symbol: string): string {
  return (symbol || "").replace(/[A-Z]\d+$/, "").toUpperCase();
}

function latestOpenTradeForSymbol(
  openTrades: OpenTrade[],
  symbol: string,
  sessionFilter: string,
): OpenTrade | undefined {
  return openTrades
    .filter(t => t.status === "open")
    .filter(t => rootSymbol(t.symbol) === symbol)
    .filter(t => sessionFilter === "all" || t.session === sessionFilter)
    .sort((a, b) => (b.opened_at ?? b.created_at ?? 0) - (a.opened_at ?? a.created_at ?? 0))[0];
}

function referenceSessionForOpenTrade(sessionFilter: string): "asia" | "us" | undefined {
  if (sessionFilter === "asia" || sessionFilter === "us") return sessionFilter;
  return activeSessionKey() ?? undefined;
}

function isOpenTradeFromReferenceSession(trade: OpenTrade | undefined, referenceSession: string | undefined): boolean | undefined {
  if (!trade || !referenceSession) return undefined;
  return trade.session?.toLowerCase() === referenceSession;
}


/* ── Intel Map Builder ── */

/**
 * buildIntelMap — Convex data → resolved IntelData per symbol.
 *
 * Data flow: Convex query results → raw IntelData → resolveIntelData() → ResolvedIntel.
 * All fallback, threshold, and merge decisions happen in resolveIntelData().
 * This function only does: (1) extract raw fields from Convex, (2) resolve, (3) add metadata.
 */
export function buildIntelMap(
  signals: Signal[],
  latestIntel: Record<string, any> | null,
  sessionFilter: string,
  latestVF: Record<string, any> | null = null,
  openTrades: OpenTrade[] = [],
) {
  const intel: Record<string, IntelData> = {};
  const symbols = [...DASHBOARD_SYMBOLS];
  const isSessionFilter = sessionFilter !== "all";

  // Check if any signals exist for this session
  const sessionSignals = isSessionFilter
    ? signals.filter(s => s.session === sessionFilter)
    : signals;
  const hasSessionSignals = sessionSignals.length > 0;
  const openTradeReferenceSession = referenceSessionForOpenTrade(sessionFilter);

  // Build per-symbol data
  for (const sym of symbols) {
    const vf = latestVF?.[sym];
    const li = latestIntel?.[sym];
    const sessionSignal = sessionSignals.find(s => rootSymbol(s.symbol) === sym);
    const activeTrade = latestOpenTradeForSymbol(openTrades, sym, sessionFilter);
    const isCurrentSessionTrade = isOpenTradeFromReferenceSession(activeTrade, openTradeReferenceSession);

    if (li) {
      const sourceTimestamp = typeof li.current_price_as_of === "number" ? li.current_price_as_of : li.created_at;
      const freshness = freshnessFromTimestamp(sourceTimestamp, LIVE_INTEL_STALE_AFTER_MS);
      const sourceStatus = dashboardDataStatusFromFreshness(li.current_price_status, freshness);
      const isLiveSnapshot = isLiveSnapshotAgent(li.agent);
      // Step 1: Extract raw fields from Convex into IntelData shape.
      // li may come from liveMarketSnapshots (agent="live", all fields from payload)
      // or from signals table (agent=orb/fbd/mr, limited fields).
      // Live snapshots are market/intel state, not executable trade records;
      // never trust payload entry/SL/TP as signal levels.
      const rawIntel: IntelData = {
        symbol: sym,
        // Direction/agent belong to the forecast/intel row. Live snapshots
        // are market state, not executable trade records, so they must not
        // borrow direction from a same-session ORB/FBD/MR signal.
        direction: isLiveSnapshot ? li.direction : (li.direction || sessionSignal?.direction),
        ...extractIntelFields(li),
        entry: isLiveSnapshot ? undefined : sessionSignal?.entry,
        stop_loss: isLiveSnapshot ? undefined : sessionSignal?.stop_loss,
        tp1: isLiveSnapshot ? undefined : sessionSignal?.tp1,
        tp2: isLiveSnapshot ? undefined : sessionSignal?.tp2,
        pnl_dollars: li.pnl_dollars,
        date: li.date,
        session: li.session,
        session_label: li.session_label,
        agent: li.agent,
        has_open_trade: activeTrade !== undefined,
        open_trade_agent: activeTrade?.agent,
        open_trade_direction: activeTrade?.direction,
        open_trade_session: activeTrade?.session,
        open_trade_current_session: openTradeReferenceSession,
        open_trade_is_current_session: isCurrentSessionTrade,
        open_trade_entry: activeTrade?.entry_price,
        open_trade_stop_loss: activeTrade?.stop_loss,
        open_trade_tp1: activeTrade?.tp1,
        open_trade_tp2: activeTrade?.tp2,
        created_at: li.created_at,
        data_status: sourceStatus,
        source_label: isLiveSnapshot
          ? `Live ${li.source ?? "hub"} ${li.date ?? ""}`.trim()
          : `Convex ${li.agent?.toUpperCase?.() ?? "signal"} ${li.date ?? ""}`.trim(),
        freshness_label: freshness.label,
      };

      // Step 2: Resolve all fallback/threshold/merge decisions in one place.
      intel[sym] = resolveIntelData(rawIntel, vf);
    } else if (isSessionFilter && !hasSessionSignals) {
      intel[sym] = { symbol: sym, pending_session: sessionFilter, data_status: "missing" };
    } else {
      intel[sym] = { symbol: sym, data_status: "missing" };
    }
  }

  return { intel, signalDate: null, hasSessionSignals };
}

/**
 * Extract intel fields from a Convex row (live snapshot or signal) into IntelData shape.
 * Only intel/data fields — no metadata (symbol, direction, entry, etc. are handled separately).
 *
 * This replaces the 60+ line manual field-by-field copy with a single spread.
 * The Convex query already returns all fields as a flat object (payload spread),
 * so we just need to pick the intel-related keys.
 */
export const COMMAND_CENTER_INTEL_KEYS: readonly (keyof IntelData)[] = [
    "hmm_regime", "hmm_probability",
    "hmm_4h_regime", "hmm_4h_probability",
    "hmm_hourly_regime", "hmm_hourly_probability",
    "hmm_composite_regime", "hmm_composite_confidence",
    "kronos_direction", "kronos_confidence", "kronos_daily_change_pct",
    "kronos_daily_predicted_close", "kronos_daily_predicted_high", "kronos_daily_predicted_low",
    "kronos_daily_valid", "kronos_daily_invalid_reason",
    "kronos_4h_direction", "kronos_4h_confidence", "kronos_4h_method", "kronos_4h_source", "kronos_4h_predicted_open", "kronos_4h_predicted_close",
    "kronos_4h_predicted_high", "kronos_4h_predicted_low", "kronos_4h_range",
    "kronos_4h_valid", "kronos_4h_invalid_reason",
    "kronos_4h_input_anchor_as_of", "kronos_4h_input_anchor_price", "kronos_4h_input_anchor_source", "kronos_4h_input_anchor_age_sec",
    "kronos_4h_anchor_status", "kronos_4h_anchor_rebased", "kronos_4h_pre_rebase_predicted_open", "kronos_4h_open_anchor_gap_pct", "kronos_4h_anchor_rebase_delta",
    "kronos_hourly_direction", "kronos_hourly_confidence", "kronos_hourly_method", "kronos_hourly_predicted_open", "kronos_hourly_predicted_close",
    "kronos_hourly_predicted_high", "kronos_hourly_predicted_low", "kronos_hourly_range",
    "kronos_hourly_valid", "kronos_hourly_invalid_reason",
    "kronos_hourly_input_anchor_as_of", "kronos_hourly_input_anchor_price", "kronos_hourly_input_anchor_source", "kronos_hourly_input_anchor_age_sec",
    "kronos_hourly_anchor_status", "kronos_hourly_anchor_rebased", "kronos_hourly_pre_rebase_predicted_open", "kronos_hourly_open_anchor_gap_pct", "kronos_hourly_anchor_rebase_delta",
    "kronos_daily_method", "kronos_daily_input_anchor_source",
    "squeeze_on", "squeeze_fired", "squeeze_momentum", "squeeze_stage", "squeeze_pct", "squeeze_tier", "squeeze_action", "squeeze_momentum_direction",
    "squeeze_1h_on", "squeeze_1h_fired", "squeeze_1h_direction", "squeeze_1h_stage", "squeeze_1h_pct", "squeeze_1h_pro_level", "squeeze_1h_status", "squeeze_1h_status_reason",
    "squeeze_4h_on", "squeeze_4h_fired", "squeeze_4h_direction", "squeeze_4h_stage", "squeeze_4h_pct", "squeeze_4h_pro_level", "squeeze_4h_status", "squeeze_4h_status_reason",
    "squeeze_daily_on", "squeeze_daily_fired", "squeeze_daily_direction", "squeeze_daily_stage", "squeeze_daily_pct", "squeeze_daily_pro_level", "squeeze_daily_status", "squeeze_daily_status_reason",
    "adx", "adx_label", "adx_plus_di", "adx_minus_di",
    "vix", "vix_regime", "vix_ma10", "vix_ma20", "vix_slope",
    "tv_rating",
    "intel_stance", "intel_alignment_score", "intel_layers_for", "intel_layers_against",
    "vol_ratio",
    "current_price", "current_price_source", "current_price_as_of", "current_price_age_sec", "current_price_status", "current_price_disagreement", "current_price_outlier_sources",
    "sma50_value", "sma50_aligned", "ema50_value", "ema50_aligned",
    "obv_divergence_10", "obv_divergence_20", "cmf_20", "mfi_14", "fbd_flow_score", "mr_flow_score",
    "iceberg_bid", "iceberg_ask", "absorption_bid", "absorption_ask", "institutional_direction", "institutional_score",
    "spoof_bid_risk", "spoof_ask_risk", "order_flow_status", "order_flow_age_sec",
    "current_bias", "order_book_bias", "volume_flow_bias", "flow_state", "flow_state_bias", "flow_state_confidence",
    "entry_action", "entry_reason", "entry_plan", "entry_blockers", "confirmation", "invalidation",
    "delta", "cumulative_tape", "data_warnings", "volume_flow_status", "volume_flow_age_sec", "volume_flow_source",
    "tape_accumulation_windows", "tape_accumulation_source", "tape_accumulation_as_of", "market_flow_stack",
    // FBE forecast fields are intentionally excluded from Command Center
    // actionability/intel cards. Forecast workspaces read raw latestIntel
    // directly so forecast diagnostics remain visible without leaking into
    // symbol action decisions.
  ];

function extractIntelFields(li: Record<string, any>): Partial<IntelData> {
  const result: Record<string, any> = {};
  for (const key of COMMAND_CENTER_INTEL_KEYS) {
    if (li[key] !== undefined) {
      result[key] = li[key];
    }
  }
  return result as Partial<IntelData>;
}

/* ── Auto-refresh hook ── */

function useAutoRefresh(intervalMs: number) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

/* ── App ── */

type AppView = "command" | "accountability" | "fbe-bias" | "markov" | "cto-review";
type AccountabilityTab = "fbe" | "orb" | "fbd" | "mr" | "execution" | "regime" | "profitability" | "data";

export const DEFAULT_APP_VIEW: AppView = "command";
export const APP_VIEW_TABS: readonly { key: AppView; label: string; adminOnly?: boolean }[] = [
  { key: "command", label: "Command Center" },
  { key: "accountability", label: "Accountability", adminOnly: true },
  { key: "fbe-bias", label: "FBE Forecast", adminOnly: true },
  { key: "markov", label: "Markov Regime", adminOnly: true },
  { key: "cto-review", label: "CTO Review", adminOnly: true },
];

export function visibleAppViewTabs(role: string) {
  const isAdmin = role === "admin";
  return APP_VIEW_TABS.filter((tab) => isAdmin || !tab.adminOnly);
}

export const ACCOUNTABILITY_TABS: readonly { key: AccountabilityTab; label: string; description: string }[] = [
  { key: "fbe", label: "FBE", description: "LOG-ONLY anchored session forecast calibration; rolling Kronos/HMM stays continuous." },
  { key: "orb", label: "ORB", description: "Recent Opening Range Breakout accountability snapshot." },
  { key: "fbd", label: "FBD", description: "Recent failed breakdown / short-trap accountability snapshot." },
  { key: "mr", label: "MR", description: "Recent mean reversion accountability snapshot." },
  { key: "execution", label: "Execution", description: "Signal-to-trade lifecycle and close-out integrity." },
  { key: "regime", label: "Regime", description: "QST-103 multi-regime validation evidence and unlock status." },
  { key: "profitability", label: "Profitability", description: "Audit-only leak, sample sufficiency, and opportunity capture evidence." },
  { key: "data", label: "Data", description: "Source wiring and quality checks for dashboard trust, separate from trading decisions." },
];

export const ORB_ACCOUNTABILITY_QUERY_ARGS = { agent: "orb", limit: 1000 } as const;
export const FBD_ACCOUNTABILITY_QUERY_ARGS = { agent: "fbd", limit: 250 } as const;
export const MR_ACCOUNTABILITY_QUERY_ARGS = { agent: "mr", limit: 250 } as const;
export const EXECUTION_ACCOUNTABILITY_QUERY_ARGS = { limit: 250 } as const;
export const CTO_REVIEW_RECIPE_QUERY_ARGS = { limit: 1000 } as const;

const ORB_ACCOUNTABILITY_CONFIG: AgentAccountabilityConfig = {
  agent: "orb",
  label: "ORB",
  description: "Opening Range Breakout accountability window.",
  sourceLabel: "getRecentSignals agent=orb limit 1000",
  showDirectionPerformance: true,
  showSessionPerformance: true,
  showSymbolPerformance: true,
};

const FBD_ACCOUNTABILITY_CONFIG: AgentAccountabilityConfig = {
  agent: "fbd",
  label: "FBD",
  description: "Failed breakdown / short-trap accountability window.",
  sourceLabel: "getRecentSignals agent=fbd limit 250",
  showFbdTrapEvidence: true,
  showCandlePatterns: true,
  showCandlePatternPerformance: true,
  showCandlePatternSignalLedger: true,
};

const MR_ACCOUNTABILITY_CONFIG: AgentAccountabilityConfig = {
  agent: "mr",
  label: "MR",
  description: "Mean reversion accountability window.",
  sourceLabel: "getRecentSignals agent=mr limit 250",
  showMrReversionEvidence: true,
  showCandlePatterns: true,
  showCandlePatternPerformance: true,
  showCandlePatternSignalLedger: true,
};

function OrbAccountabilityPanelContainer() {
  const state = useQueryWithState(api.queries.getRecentSignals, ORB_ACCOUNTABILITY_QUERY_ARGS) as QueryState<Signal[]>;
  return <AgentAccountabilityPanel state={state} config={ORB_ACCOUNTABILITY_CONFIG} />;
}

function FbdAccountabilityPanelContainer() {
  const state = useQueryWithState(api.queries.getRecentSignals, FBD_ACCOUNTABILITY_QUERY_ARGS) as QueryState<Signal[]>;
  return <AgentAccountabilityPanel state={state} config={FBD_ACCOUNTABILITY_CONFIG} />;
}

function MrAccountabilityPanelContainer() {
  const state = useQueryWithState(api.queries.getRecentSignals, MR_ACCOUNTABILITY_QUERY_ARGS) as QueryState<Signal[]>;
  return <AgentAccountabilityPanel state={state} config={MR_ACCOUNTABILITY_CONFIG} />;
}

function ExecutionAccountabilityPanelContainer() {
  const state = useQueryWithState(api.queries.getRecentSignals, EXECUTION_ACCOUNTABILITY_QUERY_ARGS) as QueryState<Signal[]>;
  return <ExecutionAccountabilityPanel state={state} />;
}

function CtoReviewWorkspaceContainer() {
  const state = useQueryWithState(api.queries.getRecentSignals, CTO_REVIEW_RECIPE_QUERY_ARGS) as QueryState<Signal[]>;
  return <CtoReviewWorkspace signalsState={state} />;
}

function RegimeValidationPanelContainer() {
  const state = useQueryWithState(api.queries.getLatestRegimeValidation, {}) as QueryState<any>;
  return <RegimeValidationPanel state={state} />;
}

function renderAccountabilityPanel(
  activeTab: AccountabilityTab,
  commandCenterDataSources: CommandCenterDataSourceRow[],
  commandCenterDataQuality: CommandCenterDataQualityRow[],
  symbols: readonly string[],
  intelMap: Record<string, IntelData | undefined>,
  openTrades: OpenTrade[],
  session: "asia" | "us" | "all",
) {
  switch (activeTab) {
    case "fbe":
      return <FbeAccountabilityPanelContainer />;
    case "execution":
      return <ExecutionAccountabilityPanelContainer />;
    case "orb":
      return <OrbAccountabilityPanelContainer />;
    case "fbd":
      return <FbdAccountabilityPanelContainer />;
    case "mr":
      return <MrAccountabilityPanelContainer />;
    case "regime":
      return <RegimeValidationPanelContainer />;
    case "profitability":
      return <ProfitabilityInsightsPanel />;
    case "data":
      return (
        <>
          <CommandCenterActionMatrix
            symbols={symbols}
            intelMap={intelMap}
            openTrades={openTrades}
            session={session}
          />
          <CommandCenterDataReadinessPanel symbols={symbols} intelMap={intelMap} />
          <CommandCenterDataWiringPanel rows={commandCenterDataSources} />
          <CommandCenterDataQualityPanel rows={commandCenterDataQuality} />
        </>
      );
  }
}

function AccountabilityView({
  activeTab,
  onTab,
  isAdmin,
  commandCenterDataSources,
  commandCenterDataQuality,
  symbols,
  intelMap,
  openTrades,
  session,
}: {
  activeTab: AccountabilityTab;
  onTab: (tab: AccountabilityTab) => void;
  isAdmin: boolean;
  commandCenterDataSources: CommandCenterDataSourceRow[];
  commandCenterDataQuality: CommandCenterDataQualityRow[];
  symbols: readonly string[];
  intelMap: Record<string, IntelData | undefined>;
  openTrades: OpenTrade[];
  session: "asia" | "us" | "all";
}) {
  const visibleTabs = ACCOUNTABILITY_TABS.filter((tab) => isAdmin || !["profitability", "data"].includes(tab.key));
  const safeActiveTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : "fbe";
  return (
    <main className="accountability-view" aria-label="Accountability dashboard">
      <section className="accountability-hero">
        <div>
          <span className="accountability-kicker">Audit layer · separated from Command Center</span>
          <h2>Accountability</h2>
          <p>Retrospective evidence lives here so the live trading cockpit stays focused on current-session action.</p>
        </div>
        <div className="accountability-status">Additive only</div>
      </section>

      <nav className="accountability-tabs" aria-label="Accountability sections">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`accountability-tab ${safeActiveTab === tab.key ? "active" : ""}`}
            onClick={() => onTab(tab.key)}
          >
            <strong>{tab.label}</strong>
            <span>{tab.description}</span>
          </button>
        ))}
      </nav>

      {/* QST-365: read-only promotion governance surface. Lives ABOVE the
          existing non-FBE Accountability panels so the standardized readiness
          / CTO action labels are visible where promotion decisions belong.
          Adds no new tab, does not render on the protected FBE Accountability
          surface, and does not modify any trading behavior. */}
      {safeActiveTab !== "fbe" ? <AccountabilityGovernancePanel tab={safeActiveTab} /> : null}

      {renderAccountabilityPanel(safeActiveTab, commandCenterDataSources, commandCenterDataQuality, symbols, intelMap, openTrades, session)}
    </main>
  );
}

export function DashboardApp({ authUser, onLogout }: { authUser: { email: string; role: string }; onLogout: () => void }) {
  const [session, setSession] = useState<"asia" | "us" | "all">("all");
  const [activeView, setActiveView] = useState<AppView>(DEFAULT_APP_VIEW);
  const [accountabilityTab, setAccountabilityTab] = useState<AccountabilityTab>("fbe");

  // Auto-refresh every 30 seconds — re-triggers Convex subscriptions
  useAutoRefresh(30_000);

  const statsQuery = useQueryWithState(api.queries.getStats, {});
  const signalsQuery = useQueryWithState(api.queries.getRecentSignals, { limit: 30 });
  const openTradesQuery = useQueryWithState(api.queries.getOpenTrades, {});
  // Keep this query reference present for the dashboard contract test; IntelRadar
  // owns the active per-symbol view, while this snapshot remains the aggregate ML surface.
  void api.queries.getIntelligenceSnapshot;
  const liveQueryArgs = session === "all" ? {} : { session };
  const intelQuery = useQueryWithState(api.queries.getLatestIntelBySymbol, liveQueryArgs);
  const vfQuery = useQueryWithState(api.queries.getLatestVolumeFlow, liveQueryArgs);
  const levelForecastQuery = useQueryWithState(api.queries.getLatestLevelBounceForecast, liveQueryArgs);
  // QST-345 / FBE-5: LOG-ONLY accountability rows are now fetched inside
  // ``FbeAccountabilityPanelContainer`` so the Convex query is scoped to the
  // panel's own (sessionTab, symbolFilter). Querying globally and filtering
  // client-side could silently evict the selected slice from the 500-row
  // cap (review FIX FIRST blocker 1).

  const statsState = statsQuery as QueryState<Stats>;
  const signalsState = signalsQuery as QueryState<Signal[]>;
  const openTradesState = openTradesQuery as QueryState<OpenTrade[]>;
  const intelState = intelQuery as QueryState<Record<string, any>>;
  const vfState = vfQuery as QueryState<Record<string, any>>;
  const levelForecastState = levelForecastQuery as QueryState<LevelBounceForecastSnapshot | null>;

  const latestIntel = intelState.status === "success" ? intelState.data : null;
  const latestVF = vfState.status === "success" ? vfState.data : null;
  const recentSignals = signalsState.status === "success" && signalsState.data ? signalsState.data : [];
  const openTrades = openTradesState.status === "success" && openTradesState.data ? openTradesState.data : [];
  // Per-symbol cards are primarily backed by getLatestIntelBySymbol.
  // Do not blank them just because getRecentSignals is still loading or failed.
  const { intel: snapshotIntelMap, signalDate, hasSessionSignals } = buildIntelMap(
    recentSignals,
    latestIntel,
    session,
    latestVF,
    openTrades,
  );
  const realtimeMarket = useRealtimeMarketFeatures(session);
  const intelMap = mergeRealtimeMarketData(snapshotIntelMap, realtimeSymbolsForMerge(realtimeMarket.status, realtimeMarket.symbols));
  const orderFlowTransportLabel = realtimeMarket.status === "live"
    ? "LIVE WS"
    : realtimeMarket.status === "disabled"
      ? "SNAPSHOT"
      : "WS FALLBACK";
  const commandCenterDataSources = buildCommandCenterDataSourceRows({
    statsState,
    signalsState,
    intelState,
    volumeFlowState: vfState,
    levelForecastState,
    orderFlowTransportLabel,
    admin: authUser.role === "admin",
  });

  const symbols = [...DASHBOARD_SYMBOLS];
  const commandCenterDataQuality = buildCommandCenterDataQualityRows(symbols, intelMap);
  const visibleViewTabs = useMemo(() => visibleAppViewTabs(authUser.role), [authUser.role]);

  useEffect(() => {
    if (!visibleViewTabs.some((tab) => tab.key === activeView)) {
      setActiveView(DEFAULT_APP_VIEW);
    }
  }, [activeView, visibleViewTabs]);

  return (
    <div className="app">
      {/* ── Header ── */}
      <header className="header">
        <div className="header-left">
          <h1 className="brand">QS TRADES</h1>
          <CountdownTimer />
          <div className="view-tabs" role="tablist" aria-label="Dashboard views">
            {visibleViewTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeView === tab.key}
                className={`view-tab ${activeView === tab.key ? "active" : ""}`}
                onClick={() => setActiveView(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="header-right">
          <div className="session-tabs">
            {(["asia", "us", "all"] as const).map(s => (
              <button
                key={s}
                className={`tab ${session === s ? "active" : ""}`}
                onClick={() => setSession(s)}
              >
                {s === "all" ? "All" : s === "asia" ? "Asia" : "US"}
              </button>
            ))}
            {signalDate && <span className="signal-date">{signalDate}</span>}
            {!hasSessionSignals && session !== "all" && <span className="signal-date pending">No {session === "asia" ? "Asia" : "US"} signals yet</span>}
          </div>
          <button
            onClick={onLogout}
            style={{
              marginLeft: 16, padding: "6px 14px", background: "#334155", border: "none",
              borderRadius: 6, color: "#94a3b8", fontSize: 12, cursor: "pointer", fontWeight: 600,
            }}
            title={`Logged in as ${authUser.email}`}
          >
            Sign Out
          </button>
        </div>
      </header>

      {activeView === "command" ? (
        <CommandCenterV2
          symbols={symbols}
          session={session}
          intelMap={intelMap}
          signals={recentSignals}
          openTrades={openTrades}
          loading={intelState.status === "loading" || signalsState.status === "loading" || openTradesState.status === "loading"}
          error={intelState.status === "error" ? intelState.error : signalsState.status === "error" ? signalsState.error : openTradesState.status === "error" ? openTradesState.error : undefined}
          transportLabel={orderFlowTransportLabel}
        />
      ) : activeView === "accountability" ? (
        <AccountabilityView
          activeTab={accountabilityTab}
          onTab={setAccountabilityTab}
          isAdmin={authUser.role === "admin"}
          commandCenterDataSources={commandCenterDataSources}
          commandCenterDataQuality={commandCenterDataQuality}
          symbols={symbols}
          intelMap={intelMap}
          openTrades={openTrades}
          session={session}
        />
      ) : activeView === "fbe-bias" ? (
        <FbeBiasWorkspace sessionFilter={session} />
      ) : activeView === "markov" ? (
        <MarkovWorkspace sessionFilter={session} />
      ) : (
        <CtoReviewWorkspaceContainer />
      )}

      {/* ── Footer ── */}
      <footer className="footer">
        <span>Powered by Novita AI (RTX 4090)</span>
        <span>Market flow: {orderFlowTransportLabel} · Convex snapshot fallback</span>
      </footer>
    </div>
  );
}

export default function App() {
  const [authUser, setAuthUser] = useState<{ email: string; role: string } | null>(() => {
    const stored = localStorage.getItem("qst_user");
    return stored ? JSON.parse(stored) : null;
  });
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("qst_token");
    if (!token) {
      setAuthChecked(true);
      return;
    }

    fetch("https://qstrades-api.aizo.workers.dev/api/verify?token=" + encodeURIComponent(token))
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setAuthUser(data.user);
        } else {
          localStorage.removeItem("qst_token");
          localStorage.removeItem("qst_user");
          setAuthUser(null);
        }
        setAuthChecked(true);
      })
      .catch(() => {
        setAuthChecked(true);
      });
  }, []);

  const handleLogin = (user: { email: string; role: string }, token: string) => {
    localStorage.setItem("qst_token", token);
    localStorage.setItem("qst_user", JSON.stringify(user));
    setAuthUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem("qst_token");
    localStorage.removeItem("qst_user");
    setAuthUser(null);
  };

  if (!authChecked) {
    return <div style={{ background: "#0f172a", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>Loading...</div>;
  }

  if (!authUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <DashboardApp authUser={authUser} onLogout={handleLogout} />;
}
